/**
 * Survey Module Configuration
 * Defines navigation, routes, and features for the Survey module.
 */

import { lazy } from 'react';
import type { ModuleConfig } from '../types';
import PollIcon from '@mui/icons-material/Poll';
import AddIcon from '@mui/icons-material/Add';
import ListIcon from '@mui/icons-material/List';

// Lazy load survey pages
const SurveyList = lazy(() => import('@/pages/SurveyList'));
const SurveyBuilder = lazy(() => import('@/pages/SurveyBuilder/SurveyBuilderSimple'));
const ResponseList = lazy(() => import('@/pages/ResponseList'));
const ResponseDetail = lazy(() => import('@/pages/ResponseDetail'));
const PublicSurvey = lazy(() => import('@/pages/PublicSurvey'));

export const surveyModuleConfig: ModuleConfig = {
  code: 'survey',
  name: 'Surveys',
  description: 'Create and manage surveys with advanced question types and conditional logic',
  version: '1.0.0',
  enabledByDefault: false, // Backend controls this via API
  icon: PollIcon,
  themeColor: '#2BC4AD',
  order: 1,

  navItems: [
    {
      id: 'surveys',
      labelKey: 'navigation.surveys',
      path: '/surveys',
      icon: PollIcon,
      order: 10,
      requiresAuth: true,
    },
  ],

  routes: [
    {
      path: '/surveys',
      component: SurveyList,
      requiresAuth: true,
      title: 'Surveys',
    },
    {
      path: '/surveys/new',
      component: SurveyBuilder,
      requiresAuth: true,
      title: 'Create Survey',
    },
    {
      path: '/surveys/:id/edit',
      component: SurveyBuilder,
      requiresAuth: true,
      title: 'Edit Survey',
    },
    {
      path: '/surveys/:surveyId/responses',
      component: ResponseList,
      requiresAuth: true,
      title: 'Survey Responses',
    },
    {
      path: '/surveys/:surveyId/responses/:responseId',
      component: ResponseDetail,
      requiresAuth: true,
      title: 'Response Detail',
    },
    {
      path: '/s/:token',
      component: PublicSurvey,
      requiresAuth: false,
      layout: 'full',
      title: 'Take Survey',
    },
  ],

  quickActions: [
    {
      id: 'create-survey',
      labelKey: 'navigation.newSurvey',
      icon: AddIcon,
      path: '/surveys/new',
      order: 1,
      variant: 'primary',
    },
    {
      id: 'view-surveys',
      labelKey: 'navigation.surveys',
      icon: ListIcon,
      path: '/surveys',
      order: 2,
      variant: 'outlined',
    },
  ],
};
